import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UnseenUpdateNotesResponse, UpdateNote } from '../types/update-note';

/** Delay before the "what's new" popup appears, so it does not fight the page transition. */
const POPUP_DELAY_MS = 1000;

@Injectable({
  providedIn: 'root',
})
export class UpdateNoteService {
  private http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/update-notes`;

  private _notes = signal<UpdateNote[] | null>(null);
  readonly notes = computed(() => this._notes() ?? []);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  private _unseen = signal<UpdateNote[]>([]);
  readonly unseen = this._unseen.asReadonly();

  private _modalVisible = signal(false);
  readonly modalVisible = this._modalVisible.asReadonly();

  /**
   * Kept in memory rather than in sessionStorage: MenuComponent is destroyed and recreated when
   * navigating /home -> /admin -> /home, so ngOnInit runs again and would re-prompt. A session
   * flag would also survive logout in the same tab, silently stealing the popup from the next
   * user. AuthService.logout() calls reset() instead. OnboardingService does the same.
   */
  private hasPromptedThisSession = false;

  /** Loads the full list once. Used by the /home/updates page. */
  loadIfNeeded(): void {
    if (this._notes() !== null || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);
    this.http.get<UpdateNote[]>(this.url).subscribe({
      next: (notes) => {
        this._notes.set(notes);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.error.set(err.message ?? 'Failed to load update notes');
        console.error(err);
      },
    });
  }

  reload(): void {
    this._notes.set(null);
    this.loadIfNeeded();
  }

  /**
   * Fetches the notes published since the user last dismissed the popup and opens it if there are
   * any. Safe to call on every mount — it prompts at most once per session.
   */
  checkUnseenAndPrompt(): void {
    if (this.hasPromptedThisSession) {
      return;
    }
    this.hasPromptedThisSession = true;

    this.http.get<UnseenUpdateNotesResponse>(`${this.url}/unseen`).subscribe({
      next: (response) => {
        this._unseen.set(response.notes);
        if (response.count > 0) {
          setTimeout(() => this._modalVisible.set(true), POPUP_DELAY_MS);
        }
      },
      error: (err) => console.error(err),
    });
  }

  /** Acknowledges every unseen note. Closing the popup by any means calls this. */
  markSeen(): Observable<void> {
    return this.http.post<void>(`${this.url}/seen`, {}).pipe(
      tap(() => {
        this._unseen.set([]);
        this._modalVisible.set(false);
      }),
      map(() => void 0),
    );
  }

  closeModal(): void {
    this._modalVisible.set(false);
  }

  /** Called on logout so the next user in the same tab gets their own popup. */
  reset(): void {
    this._notes.set(null);
    this._unseen.set([]);
    this._modalVisible.set(false);
    this.error.set(null);
    this.hasPromptedThisSession = false;
  }
}
